import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Clock, MapPin, Star } from "lucide-react";
import { Button } from "../ui/button";
import { Starburst } from "../common";
import { useLead } from "../LeadDialog";
import { CONTACT, LOGO_URL, NAV_LINKS, BADGES } from "../../data/site";
import { SERVICE_PAGES } from "../../data/servicePages";

const Footer = () => {
  const { openLead } = useLead();
  const { pathname } = useLocation();
  const linkHref = (id) => (pathname === "/" ? `#${id}` : `/#${id}`);
  return (
    <footer
      id="contact"
      className="bg-jet-teal text-white scroll-mt-20"
      data-testid="footer"
    >
      {/* Contact band */}
      <div className="max-w-[88rem] mx-auto px-5 md:px-10 pt-20 md:pt-28 pb-10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-jet-amber mb-5">
              <Starburst className="h-3 w-3 text-jet-amber" /> Get in Touch
            </div>
            <a
              href={CONTACT.phoneHref}
              className="block font-display font-semibold leading-[0.9] tracking-tight text-white hover:text-jet-ink transition-colors text-[13vw] lg:text-[6vw]"
              data-testid="footer-phone"
            >
              {CONTACT.phoneDisplay}
            </a>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-white/90 font-medium">
              <span className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-jet-amber" /> Dallas/Fort Worth, Texas
              </span>
              <span className="inline-flex items-center gap-2">
                <Clock className="h-4 w-4 text-jet-amber" /> {CONTACT.hours}
              </span>
              <a
                href={CONTACT.googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-white/70 transition-colors"
                data-testid="footer-google-link"
              >
                <Star className="h-4 w-4 text-jet-amber" /> Find us on Google
              </a>
            </div>
          </div>

          <div className="lg:justify-self-end w-full lg:max-w-md rounded-3xl bg-white shadow-xl p-8">
            <h3 className="font-display text-2xl font-semibold mb-2 text-jet-ink">
              Ready when you are.
            </h3>
            <p className="text-jet-ink/60 mb-6">
              Request a quote and our team will get back to you fast with honest
              pricing.
            </p>
            <Button
              onClick={() => openLead("quote")}
              className="w-full rounded-full bg-jet-amber hover:bg-jet-amber/90 text-white font-bold h-13 py-3 shadow-lg"
              data-testid="footer-quote-button"
            >
              Request a Quote
            </Button>
            <a
              href={CONTACT.phoneHref}
              className="mt-4 inline-flex items-center justify-center gap-2 w-full font-bold text-jet-ink hover:text-jet-teal transition-colors"
              data-testid="footer-call"
            >
              <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
            </a>
          </div>
        </div>

        {/* Popular services (internal links for SEO) */}
        <div className="mt-12 border-t border-white/25 pt-8">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-jet-amber mb-4">
            Popular Services
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2.5">
            {SERVICE_PAGES.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                className="text-sm text-white/85 hover:text-white transition-colors"
                data-testid={`footer-service-${s.slug}`}
              >
                {s.short}
              </Link>
            ))}
          </div>
        </div>

        {/* Badges */}
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/25 pt-8">
          {BADGES.map((b) => (
            <div
              key={b}
              className="flex items-center gap-2 text-sm font-semibold text-white"
            >
              <Starburst className="h-3.5 w-3.5 text-jet-amber" /> {b}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/25">
        <div className="max-w-[88rem] mx-auto px-5 md:px-10 py-7 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Jet Cleaning Service"
              className="h-16 w-16 rounded-xl object-cover"
            />
            <div className="leading-none">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jet-amber font-bold">
                The Future of Cleaning
              </div>
            </div>
          </Link>
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={linkHref(l.id)}
                className="text-sm text-white/80 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="text-sm text-white/70">
            © {new Date().getFullYear()} Jet Cleaning Service
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
